import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { schedulingLinksApi, SchedulingLink, SchedulingLinkFormData } from '../services/api';
import { detectTimezone, TIMEZONES } from '../utils/timezones';
import { useAuth } from '../context/AuthContext';
import {
    Link as LinkIcon,
    Plus,
    Copy,
    Check,
    Clock,
    Calendar,
    Settings,
    ToggleLeft,
    ToggleRight,
    Trash2,
    Edit,
    Users,
    ExternalLink,
    X,
    Ban,
    CalendarX,
    Zap,
    ClipboardList,
    Sun,
    Moon,
    Monitor,
    Upload,
    Image as ImageIcon,
    Bell,
    Eye,
    Globe
} from 'lucide-react';

const DAYS_OF_WEEK = [
    { value: null, label: 'Every day' },
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
];

const TIMES = Array.from({ length: 24 }, (_, h) => {
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const period = h < 12 ? 'AM' : 'PM';
    return { value: `${h.toString().padStart(2, '0')}:00:00`, label: `${hour12}:00 ${period}` };
});

const DURATIONS = [
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 45, label: '45 minutes' },
    { value: 60, label: '1 hour' },
    { value: 90, label: '1.5 hours' },
    { value: 120, label: '2 hours' },
];

const REMINDER_OPTIONS = [
    { value: 15, label: '15 minutes before' },
    { value: 30, label: '30 minutes before' },
    { value: 60, label: '1 hour before' },
    { value: 120, label: '2 hours before' },
    { value: 1440, label: '1 day before' },
    { value: 2880, label: '2 days before' },
];

export function SchedulingLinksPage() {
    const [links, setLinks] = useState<SchedulingLink[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingLink, setEditingLink] = useState<SchedulingLink | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    useEffect(() => {
        loadLinks();
    }, []);

    const loadLinks = async () => {
        try {
            const data = await schedulingLinksApi.list();
            setLinks(data);
        } catch (err) {
            console.error('Failed to load scheduling links:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyLink = async (link: SchedulingLink) => {
        try {
            await navigator.clipboard.writeText(link.booking_url);
            setCopiedId(link.id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleToggle = async (link: SchedulingLink) => {
        try {
            await schedulingLinksApi.toggle(link.id);
            loadLinks();
        } catch (err: any) {
            alert(err.message || 'Failed to toggle link');
        }
    };

    const handleDelete = async (link: SchedulingLink) => {
        if (!confirm(`Delete "${link.name}"? This cannot be undone.`)) return;
        try {
            await schedulingLinksApi.delete(link.id);
            loadLinks();
        } catch (err: any) {
            alert(err.message || 'Failed to delete link');
        }
    };

    const handleCreateOrUpdate = async (data: SchedulingLinkFormData) => {
        try {
            if (editingLink) {
                await schedulingLinksApi.update(editingLink.id, data);
            } else {
                await schedulingLinksApi.create(data);
            }
            setShowCreateModal(false);
            setEditingLink(null);
            loadLinks();
        } catch (err: any) {
            alert(err.message || 'Failed to save');
        }
    };

    if (isLoading) {
        return (
            <div className="page container">
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    return (
        <div className="page container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-6)' }}>
                <div>
                    <h1 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <LinkIcon size={28} />
                        Scheduling Links
                    </h1>
                    <p style={{ color: 'var(--color-gray-500)', marginTop: 'var(--space-1)' }}>
                        Create shareable links for others to propose meeting times
                    </p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="btn btn-primary"
                >
                    <Plus size={18} />
                    Create Link
                </button>
            </div>

            {links.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                    <LinkIcon size={48} style={{ color: 'var(--color-gray-300)', margin: '0 auto var(--space-4)' }} />
                    <h3>No scheduling links yet</h3>
                    <p style={{ color: 'var(--color-gray-500)', marginBottom: 'var(--space-4)' }}>
                        Create a scheduling link to let others propose meeting times based on your calendar availability.
                    </p>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="btn btn-primary"
                    >
                        <Plus size={18} />
                        Create Your First Link
                    </button>
                </div>
            ) : (
                <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
                    {links.map(link => (
                        <div
                            key={link.id}
                            className="card"
                            style={{
                                opacity: link.is_active ? 1 : 0.6,
                                borderLeft: `4px solid ${link.is_active ? 'var(--color-primary)' : 'var(--color-gray-300)'}`
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                        <h3 style={{ margin: 0 }}>{link.name}</h3>
                                        <span style={{
                                            fontSize: 'var(--font-size-xs)',
                                            background: link.booking_type === 'direct' ? '#dcfce7' : 'var(--color-gray-100)',
                                            color: link.booking_type === 'direct' ? '#16a34a' : 'var(--color-gray-600)',
                                            padding: '2px 8px',
                                            borderRadius: 'var(--radius-full)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            {link.booking_type === 'direct' ? <Zap size={10} /> : <ClipboardList size={10} />}
                                            {link.booking_type === 'direct' ? 'Instant' : 'Propose'}
                                        </span>
                                        {!link.is_active && (
                                            <span style={{
                                                fontSize: 'var(--font-size-xs)',
                                                background: 'var(--color-gray-200)',
                                                padding: '2px 8px',
                                                borderRadius: 'var(--radius-full)'
                                            }}>
                                                Inactive
                                            </span>
                                        )}
                                        {link.pending_count > 0 && (
                                            <span style={{
                                                fontSize: 'var(--font-size-xs)',
                                                background: 'var(--color-primary)',
                                                color: 'white',
                                                padding: '2px 8px',
                                                borderRadius: 'var(--radius-full)',
                                                fontWeight: 600
                                            }}>
                                                {link.pending_count} pending
                                            </span>
                                        )}
                                    </div>

                                    {link.description && (
                                        <p style={{ color: 'var(--color-gray-500)', marginBottom: 'var(--space-3)' }}>
                                            {link.description}
                                        </p>
                                    )}

                                    <div style={{ display: 'flex', gap: 'var(--space-4)', fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Clock size={14} />
                                            {link.duration_minutes} min
                                        </span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Calendar size={14} />
                                            {link.advance_days_min}-{link.advance_days_max} days out
                                        </span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Users size={14} />
                                            {link.total_proposals} total proposals
                                        </span>
                                    </div>

                                    <div style={{
                                        marginTop: 'var(--space-3)',
                                        padding: 'var(--space-2) var(--space-3)',
                                        background: 'var(--color-gray-100)',
                                        borderRadius: 'var(--radius-sm)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)',
                                        fontSize: 'var(--font-size-sm)'
                                    }}>
                                        <code style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {link.booking_url}
                                        </code>
                                        <button
                                            onClick={() => handleCopyLink(link)}
                                            className="btn btn-sm btn-secondary"
                                            style={{ flexShrink: 0 }}
                                        >
                                            {copiedId === link.id ? <Check size={14} /> : <Copy size={14} />}
                                            {copiedId === link.id ? 'Copied!' : 'Copy'}
                                        </button>
                                        <a
                                            href={link.booking_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-secondary"
                                            style={{ flexShrink: 0 }}
                                        >
                                            <ExternalLink size={14} />
                                        </a>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: 'var(--space-2)', marginLeft: 'var(--space-4)' }}>
                                    <button
                                        onClick={() => handleToggle(link)}
                                        className="btn btn-sm btn-secondary"
                                        title={link.is_active ? 'Deactivate' : 'Activate'}
                                    >
                                        {link.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setEditingLink(link);
                                            setShowCreateModal(true);
                                        }}
                                        className="btn btn-sm btn-secondary"
                                        title="Edit"
                                    >
                                        <Edit size={18} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(link)}
                                        className="btn btn-sm btn-secondary"
                                        title="Delete"
                                        style={{ color: 'var(--color-error)' }}
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>

                            {link.pending_count > 0 && (
                                <Link
                                    to={`/proposals?link_id=${link.id}`}
                                    style={{
                                        display: 'block',
                                        marginTop: 'var(--space-3)',
                                        padding: 'var(--space-2)',
                                        background: 'var(--color-primary-light)',
                                        borderRadius: 'var(--radius-sm)',
                                        textAlign: 'center',
                                        color: 'var(--color-primary-hover)',
                                        fontWeight: 500,
                                        fontSize: 'var(--font-size-sm)'
                                    }}
                                >
                                    View {link.pending_count} pending proposal{link.pending_count !== 1 ? 's' : ''}
                                </Link>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            {showCreateModal && (
                <LinkFormModal
                    link={editingLink}
                    onClose={() => {
                        setShowCreateModal(false);
                        setEditingLink(null);
                    }}
                    onSubmit={handleCreateOrUpdate}
                />
            )}
        </div>
    );
}

// Link Form Modal Component
function LinkFormModal({
    link,
    onClose,
    onSubmit
}: {
    link: SchedulingLink | null;
    onClose: () => void;
    onSubmit: (data: SchedulingLinkFormData) => Promise<void>;
}) {
    const [name, setName] = useState(link?.name || '');
    const [description, setDescription] = useState(link?.description || '');
    const [bookingType, setBookingType] = useState<'proposal' | 'direct'>(link?.booking_type || 'proposal');
    const [durationMinutes, setDurationMinutes] = useState(link?.duration_minutes || 30);
    const [bufferBefore, setBufferBefore] = useState(link?.buffer_before || 0);
    const [bufferAfter, setBufferAfter] = useState(link?.buffer_after || 0);
    const [advanceDaysMin, setAdvanceDaysMin] = useState(link?.advance_days_min || 1);
    const [advanceDaysMax, setAdvanceDaysMax] = useState(link?.advance_days_max || 28);
    const [maxProposals, setMaxProposals] = useState(link?.max_proposals || 3);
    const [defaultTheme, setDefaultTheme] = useState<'dark' | 'light' | 'system'>(link?.default_theme || 'dark');
    const [timezone, setTimezone] = useState<string>(link?.timezone || detectTimezone());
    const [hubspotEnabled, setHubspotEnabled] = useState(link?.hubspot_enabled || false);
    const [isSaving, setIsSaving] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showBlocking, setShowBlocking] = useState(false);
    const [showReminders, setShowReminders] = useState(false);
    
    const { hubspotConnected } = useAuth();

    // Reminders state
    const [reminders, setReminders] = useState<number[]>(link?.reminders || []);
    const [reminderEmailBody, setReminderEmailBody] = useState<string>(link?.reminder_email_body || '');
    const [showEmailTemplate, setShowEmailTemplate] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewHtml, setPreviewHtml] = useState<string>('');
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);

    // Logo state
    const [logoUrl, setLogoUrl] = useState<string | null>(link?.logo_url || null);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(link?.logo_url || null);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);

    // Blocked dates state
    const [blockedDates, setBlockedDates] = useState<{ blocked_date: string; reason?: string }[]>(
        link?.blocked_dates?.map(d => ({ blocked_date: d.blocked_date, reason: d.reason || undefined })) || []
    );
    const [newBlockedDate, setNewBlockedDate] = useState('');
    const [newBlockedDateReason, setNewBlockedDateReason] = useState('');

    // Blocked times state
    const [blockedTimes, setBlockedTimes] = useState<{ day_of_week: number | null; start_time: string; end_time: string; reason?: string }[]>(
        link?.blocked_times?.map(t => ({ 
            day_of_week: t.day_of_week, 
            start_time: t.start_time, 
            end_time: t.end_time, 
            reason: t.reason || undefined 
        })) || []
    );
    const [newTimeDay, setNewTimeDay] = useState<number | null>(null);
    const [newTimeStart, setNewTimeStart] = useState('12:00:00');
    const [newTimeEnd, setNewTimeEnd] = useState('13:00:00');
    const [newTimeReason, setNewTimeReason] = useState('');

    const addBlockedDate = () => {
        if (!newBlockedDate) return;
        if (blockedDates.some(d => d.blocked_date === newBlockedDate)) {
            alert('This date is already blocked');
            return;
        }
        setBlockedDates([...blockedDates, { blocked_date: newBlockedDate, reason: newBlockedDateReason || undefined }]);
        setNewBlockedDate('');
        setNewBlockedDateReason('');
    };

    const removeBlockedDate = (index: number) => {
        setBlockedDates(blockedDates.filter((_, i) => i !== index));
    };

    const addBlockedTime = () => {
        if (newTimeStart >= newTimeEnd) {
            alert('End time must be after start time');
            return;
        }
        setBlockedTimes([...blockedTimes, { 
            day_of_week: newTimeDay, 
            start_time: newTimeStart, 
            end_time: newTimeEnd,
            reason: newTimeReason || undefined
        }]);
        setNewTimeReason('');
    };

    const removeBlockedTime = (index: number) => {
        setBlockedTimes(blockedTimes.filter((_, i) => i !== index));
    };

    const toggleReminder = (minutes: number) => {
        if (reminders.includes(minutes)) {
            setReminders(reminders.filter(m => m !== minutes));
        } else {
            setReminders([...reminders, minutes].sort((a, b) => a - b));
        }
    };

    const formatTime = (time: string) => {
        const [h] = time.split(':');
        const hour = parseInt(h);
        const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const period = hour < 12 ? 'AM' : 'PM';
        return `${hour12}:00 ${period}`;
    };

    const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            alert('Please select a valid image file (PNG, JPG, GIF, or WebP)');
            return;
        }

        // Validate file size (2MB max)
        if (file.size > 2 * 1024 * 1024) {
            alert('Logo file must be less than 2MB');
            return;
        }

        setLogoFile(file);
        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        setLogoPreview(previewUrl);
    };

    const handleLogoUpload = async () => {
        if (!logoFile || !link) return;

        setIsUploadingLogo(true);
        try {
            const updatedLink = await schedulingLinksApi.uploadLogo(link.id, logoFile);
            setLogoUrl(updatedLink.logo_url || null);
            setLogoPreview(updatedLink.logo_url || null);
            setLogoFile(null);
        } catch (err: any) {
            alert(err.message || 'Failed to upload logo');
        } finally {
            setIsUploadingLogo(false);
        }
    };

    const handleLogoDelete = async () => {
        if (!link || !logoUrl) return;
        
        if (!confirm('Delete the logo?')) return;

        setIsUploadingLogo(true);
        try {
            await schedulingLinksApi.deleteLogo(link.id);
            setLogoUrl(null);
            setLogoPreview(null);
            setLogoFile(null);
        } catch (err: any) {
            alert(err.message || 'Failed to delete logo');
        } finally {
            setIsUploadingLogo(false);
        }
    };

    const clearLogoSelection = () => {
        setLogoFile(null);
        if (logoUrl) {
            setLogoPreview(logoUrl);
        } else {
            setLogoPreview(null);
        }
    };

    const handlePreviewEmail = async () => {
        setIsLoadingPreview(true);
        try {
            const html = await schedulingLinksApi.previewReminderEmail({
                meeting_name: name.trim() || 'Meeting',
                duration_minutes: durationMinutes,
                minutes_before: reminders[0] || 15,
                theme: defaultTheme === 'system' ? 'dark' : defaultTheme,
                custom_body: reminderEmailBody.trim() || null,
                include_teams_link: true,
            });
            setPreviewHtml(html);
            setShowPreviewModal(true);
        } catch (err: any) {
            alert(err.message || 'Failed to generate preview');
        } finally {
            setIsLoadingPreview(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            alert('Please enter a name for the link');
            return;
        }

        setIsSaving(true);
        try {
            await onSubmit({
                name: name.trim(),
                description: description.trim() || undefined,
                booking_type: bookingType,
                duration_minutes: durationMinutes,
                buffer_before: bufferBefore,
                buffer_after: bufferAfter,
                advance_days_min: advanceDaysMin,
                advance_days_max: advanceDaysMax,
                max_proposals: maxProposals,
                default_theme: defaultTheme,
                timezone: timezone,
                hubspot_enabled: hubspotEnabled,
                blocked_dates: blockedDates,
                blocked_times: blockedTimes,
                reminders: reminders,
                reminder_email_body: reminderEmailBody.trim() || null
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 'var(--space-4)'
        }}>
            <div className="card" style={{ maxWidth: '900px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
                    <h2 style={{ margin: 0 }}>{link ? 'Edit' : 'Create'} Scheduling Link</h2>
                    <button onClick={onClose} className="btn btn-secondary btn-sm">
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label className="form-label">Link Name *</label>
                        <input
                            type="text"
                            className="form-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., 30-min Discovery Call"
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Description (optional)</label>
                        <textarea
                            className="form-textarea"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Brief description shown to requesters"
                            rows={2}
                        />
                    </div>

                    {/* Logo Upload */}
                    <div className="form-group">
                        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <ImageIcon size={14} />
                            Logo (optional)
                        </label>
                        <p className="form-hint" style={{ marginBottom: 'var(--space-2)' }}>
                            Add a logo to brand your booking page. Max 2MB (PNG, JPG, GIF, WebP)
                        </p>
                        
                        {logoPreview ? (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-3)',
                                padding: 'var(--space-3)',
                                background: 'var(--color-gray-50)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--color-gray-200)'
                            }}>
                                <img
                                    src={logoPreview}
                                    alt="Logo preview"
                                    style={{
                                        width: '64px',
                                        height: '64px',
                                        objectFit: 'contain',
                                        borderRadius: 'var(--radius-sm)',
                                        background: 'white'
                                    }}
                                />
                                <div style={{ flex: 1 }}>
                                    {logoFile ? (
                                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>
                                            {logoFile.name}
                                        </span>
                                    ) : (
                                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
                                            Current logo
                                        </span>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                    {logoFile && link && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={handleLogoUpload}
                                                disabled={isUploadingLogo}
                                                className="btn btn-sm btn-primary"
                                            >
                                                {isUploadingLogo ? 'Uploading...' : 'Upload'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={clearLogoSelection}
                                                className="btn btn-sm btn-secondary"
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    )}
                                    {!logoFile && logoUrl && (
                                        <button
                                            type="button"
                                            onClick={handleLogoDelete}
                                            disabled={isUploadingLogo}
                                            className="btn btn-sm btn-secondary"
                                            style={{ color: 'var(--color-error)' }}
                                        >
                                            <Trash2 size={14} />
                                            Remove
                                        </button>
                                    )}
                                    {!logoFile && (
                                        <label className="btn btn-sm btn-secondary" style={{ cursor: 'pointer' }}>
                                            <Upload size={14} />
                                            Change
                                            <input
                                                type="file"
                                                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                                                onChange={handleLogoSelect}
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <label style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                padding: 'var(--space-4)',
                                background: 'var(--color-gray-50)',
                                borderRadius: 'var(--radius-md)',
                                border: '2px dashed var(--color-gray-300)',
                                cursor: 'pointer',
                                transition: 'all var(--transition-fast)'
                            }}>
                                <Upload size={24} style={{ color: 'var(--color-gray-400)' }} />
                                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
                                    {link ? 'Click to upload a logo' : 'Save the link first, then add a logo'}
                                </span>
                                {link && (
                                    <input
                                        type="file"
                                        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                                        onChange={handleLogoSelect}
                                        style={{ display: 'none' }}
                                    />
                                )}
                            </label>
                        )}
                    </div>

                    {/* Booking Type Selector */}
                    <div className="form-group">
                        <label className="form-label">Booking Mode</label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
                            <button
                                type="button"
                                onClick={() => setBookingType('proposal')}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    padding: 'var(--space-3)',
                                    border: `2px solid ${bookingType === 'proposal' ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                                    borderRadius: 'var(--radius-md)',
                                    background: bookingType === 'proposal' ? 'var(--color-primary-light)' : 'white',
                                    cursor: 'pointer',
                                    transition: 'all var(--transition-fast)'
                                }}
                            >
                                <ClipboardList size={24} style={{ color: bookingType === 'proposal' ? 'var(--color-primary)' : 'var(--color-gray-400)' }} />
                                <span style={{ fontWeight: 600, color: bookingType === 'proposal' ? 'var(--color-primary)' : 'var(--color-gray-700)' }}>
                                    Propose Times
                                </span>
                                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)', textAlign: 'center' }}>
                                    Invitees suggest times for you to approve
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setBookingType('direct')}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    padding: 'var(--space-3)',
                                    border: `2px solid ${bookingType === 'direct' ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                                    borderRadius: 'var(--radius-md)',
                                    background: bookingType === 'direct' ? 'var(--color-primary-light)' : 'white',
                                    cursor: 'pointer',
                                    transition: 'all var(--transition-fast)'
                                }}
                            >
                                <Zap size={24} style={{ color: bookingType === 'direct' ? 'var(--color-primary)' : 'var(--color-gray-400)' }} />
                                <span style={{ fontWeight: 600, color: bookingType === 'direct' ? 'var(--color-primary)' : 'var(--color-gray-700)' }}>
                                    Instant Booking
                                </span>
                                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)', textAlign: 'center' }}>
                                    Invitees book directly with Teams meeting
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* HubSpot Integration Toggle (only for direct booking) */}
                    {bookingType === 'direct' && (
                        <div className="form-group" style={{
                            padding: 'var(--space-3)',
                            background: hubspotEnabled ? '#fef3c7' : 'var(--color-gray-50)',
                            border: `1px solid ${hubspotEnabled ? '#fbbf24' : 'var(--color-gray-200)'}`,
                            borderRadius: 'var(--radius-md)'
                        }}>
                            <label style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                cursor: hubspotConnected ? 'pointer' : 'not-allowed',
                                opacity: hubspotConnected ? 1 : 0.6
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M18.7 7.3c-.4-.4-.9-.6-1.5-.6h-1.4V5.5c0-1.4-1.1-2.5-2.5-2.5h-2.5c-1.4 0-2.5 1.1-2.5 2.5v1.2H6.8c-.6 0-1.1.2-1.5.6-.4.4-.6.9-.6 1.5v10.4c0 .6.2 1.1.6 1.5.4.4.9.6 1.5.6h10.4c.6 0 1.1-.2 1.5-.6.4-.4.6-.9.6-1.5V8.8c0-.6-.2-1.1-.6-1.5zM9.9 5.5c0-.5.4-.9.9-.9h2.5c.5 0 .9.4.9.9v1.2H9.9V5.5zm8.4 13.7c0 .2-.1.4-.2.5-.1.1-.3.2-.5.2H6.8c-.2 0-.4-.1-.5-.2-.1-.1-.2-.3-.2-.5V8.8c0-.2.1-.4.2-.5.1-.1.3-.2.5-.2h10.4c.2 0 .4.1.5.2.1.1.2.3.2.5v10.4z" fill="currentColor"/>
                                        <circle cx="12" cy="14" r="2" fill="currentColor"/>
                                    </svg>
                                    <div>
                                        <span style={{ fontWeight: 600 }}>Sync to HubSpot CRM</span>
                                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-600)', marginTop: '2px' }}>
                                            {hubspotConnected 
                                                ? 'Create contacts and log meetings automatically' 
                                                : 'Connect HubSpot in settings to enable'
                                            }
                                        </div>
                                    </div>
                                </div>
                                <input
                                    type="checkbox"
                                    className="toggle toggle-primary"
                                    checked={hubspotEnabled}
                                    onChange={(e) => setHubspotEnabled(e.target.checked)}
                                    disabled={!hubspotConnected}
                                />
                            </label>
                        </div>
                    )}

                    <div className="form-group">
                        <label className="form-label">Duration</label>
                        <select
                            className="form-select"
                            value={durationMinutes}
                            onChange={(e) => setDurationMinutes(Number(e.target.value))}
                        >
                            {DURATIONS.map(d => (
                                <option key={d.value} value={d.value}>{d.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Default Theme Selector */}
                    <div className="form-group">
                        <label className="form-label">Default Theme</label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-2)' }}>
                            <button
                                type="button"
                                onClick={() => setDefaultTheme('dark')}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 'var(--space-1)',
                                    padding: 'var(--space-2)',
                                    border: `2px solid ${defaultTheme === 'dark' ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                                    borderRadius: 'var(--radius-md)',
                                    background: defaultTheme === 'dark' ? 'var(--color-primary-light)' : 'white',
                                    cursor: 'pointer',
                                    transition: 'all var(--transition-fast)'
                                }}
                            >
                                <Moon size={20} style={{ color: defaultTheme === 'dark' ? 'var(--color-primary)' : 'var(--color-gray-400)' }} />
                                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 500, color: defaultTheme === 'dark' ? 'var(--color-primary)' : 'var(--color-gray-600)' }}>
                                    Dark
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setDefaultTheme('light')}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 'var(--space-1)',
                                    padding: 'var(--space-2)',
                                    border: `2px solid ${defaultTheme === 'light' ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                                    borderRadius: 'var(--radius-md)',
                                    background: defaultTheme === 'light' ? 'var(--color-primary-light)' : 'white',
                                    cursor: 'pointer',
                                    transition: 'all var(--transition-fast)'
                                }}
                            >
                                <Sun size={20} style={{ color: defaultTheme === 'light' ? 'var(--color-primary)' : 'var(--color-gray-400)' }} />
                                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 500, color: defaultTheme === 'light' ? 'var(--color-primary)' : 'var(--color-gray-600)' }}>
                                    Light
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setDefaultTheme('system')}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 'var(--space-1)',
                                    padding: 'var(--space-2)',
                                    border: `2px solid ${defaultTheme === 'system' ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                                    borderRadius: 'var(--radius-md)',
                                    background: defaultTheme === 'system' ? 'var(--color-primary-light)' : 'white',
                                    cursor: 'pointer',
                                    transition: 'all var(--transition-fast)'
                                }}
                            >
                                <Monitor size={20} style={{ color: defaultTheme === 'system' ? 'var(--color-primary)' : 'var(--color-gray-400)' }} />
                                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 500, color: defaultTheme === 'system' ? 'var(--color-primary)' : 'var(--color-gray-600)' }}>
                                    System
                                </span>
                            </button>
                        </div>
                        <p className="form-hint">
                            Theme shown to visitors. They can still toggle between themes.
                        </p>
                    </div>

                    {/* Timezone Selector */}
                    <div className="form-group">
                        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Globe size={14} />
                            Your Timezone
                        </label>
                        <select
                            className="form-select"
                            value={timezone}
                            onChange={(e) => setTimezone(e.target.value)}
                        >
                            {TIMEZONES.map(tz => (
                                <option key={tz.value} value={tz.value}>
                                    {tz.label}
                                </option>
                            ))}
                        </select>
                        <p className="form-hint">
                            Blocked times will be in this timezone
                        </p>
                    </div>

                    {bookingType === 'proposal' && (
                        <div className="form-group">
                            <label className="form-label">Max time preferences per request</label>
                            <select
                                className="form-select"
                                value={maxProposals}
                                onChange={(e) => setMaxProposals(Number(e.target.value))}
                            >
                                {[1, 2, 3, 4, 5].map(n => (
                                    <option key={n} value={n}>
                                        {n} time{n > 1 ? 's' : ''}
                                    </option>
                                ))}
                            </select>
                            <p className="form-hint">
                                How many time options can requesters propose
                            </p>
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-primary)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginBottom: 'var(--space-4)',
                            fontSize: 'var(--font-size-sm)'
                        }}
                    >
                        <Settings size={14} />
                        {showAdvanced ? 'Hide' : 'Show'} advanced settings
                    </button>

                    {showAdvanced && (
                        <>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                                <div className="form-group">
                                    <label className="form-label">Availability starts</label>
                                    <select
                                        className="form-select"
                                        value={advanceDaysMin}
                                        onChange={(e) => setAdvanceDaysMin(Number(e.target.value))}
                                    >
                                        <option value={0.25}>In 6 hours</option>
                                        <option value={0.5}>In 12 hours</option>
                                        <option value={0}>Today</option>
                                        <option value={1}>Tomorrow</option>
                                        <option value={2}>In 2 days</option>
                                        <option value={3}>In 3 days</option>
                                        <option value={7}>In 1 week</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Availability ends</label>
                                    <select
                                        className="form-select"
                                        value={advanceDaysMax}
                                        onChange={(e) => setAdvanceDaysMax(Number(e.target.value))}
                                    >
                                        <option value={7}>1 week out</option>
                                        <option value={14}>2 weeks out</option>
                                        <option value={21}>3 weeks out</option>
                                        <option value={28}>4 weeks out</option>
                                        <option value={60}>2 months out</option>
                                        <option value={90}>3 months out</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                                <div className="form-group">
                                    <label className="form-label">Buffer before meeting</label>
                                    <select
                                        className="form-select"
                                        value={bufferBefore}
                                        onChange={(e) => setBufferBefore(Number(e.target.value))}
                                    >
                                        <option value={0}>None</option>
                                        <option value={5}>5 minutes</option>
                                        <option value={10}>10 minutes</option>
                                        <option value={15}>15 minutes</option>
                                        <option value={30}>30 minutes</option>
                                        <option value={60}>1 hour</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Buffer after meeting</label>
                                    <select
                                        className="form-select"
                                        value={bufferAfter}
                                        onChange={(e) => setBufferAfter(Number(e.target.value))}
                                    >
                                        <option value={0}>None</option>
                                        <option value={5}>5 minutes</option>
                                        <option value={10}>10 minutes</option>
                                        <option value={15}>15 minutes</option>
                                        <option value={30}>30 minutes</option>
                                        <option value={60}>1 hour</option>
                                    </select>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Booking Reminders Section */}
                    <button
                        type="button"
                        onClick={() => setShowReminders(!showReminders)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-primary)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginBottom: 'var(--space-4)',
                            fontSize: 'var(--font-size-sm)'
                        }}
                    >
                        <Bell size={14} />
                        {showReminders ? 'Hide' : 'Show'} booking reminders
                        {reminders.length > 0 && (
                            <span style={{
                                background: 'var(--color-primary)',
                                color: 'white',
                                borderRadius: '50%',
                                width: '18px',
                                height: '18px',
                                fontSize: '11px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginLeft: '4px'
                            }}>
                                {reminders.length}
                            </span>
                        )}
                    </button>

                    {showReminders && (
                        <div style={{ 
                            background: 'var(--color-gray-50)', 
                            padding: 'var(--space-4)', 
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 'var(--space-2)' }}>
                                <Bell size={16} />
                                Email Reminders
                            </h4>
                            <p className="form-hint" style={{ marginBottom: 'var(--space-3)' }}>
                                Send email reminders to the person who booked before their meeting. Select multiple to send at different times.
                            </p>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                                {REMINDER_OPTIONS.map(option => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => toggleReminder(option.value)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: 'var(--space-2) var(--space-3)',
                                            border: `2px solid ${reminders.includes(option.value) ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                                            borderRadius: 'var(--radius-full)',
                                            background: reminders.includes(option.value) ? 'var(--color-primary-light)' : 'white',
                                            color: reminders.includes(option.value) ? 'var(--color-primary)' : 'var(--color-gray-600)',
                                            cursor: 'pointer',
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: reminders.includes(option.value) ? 600 : 400,
                                            transition: 'all var(--transition-fast)'
                                        }}
                                    >
                                        {reminders.includes(option.value) && <Check size={14} />}
                                        {option.label}
                                    </button>
                                ))}
                            </div>

                            {reminders.length === 0 && (
                                <p style={{ 
                                    marginTop: 'var(--space-3)', 
                                    color: 'var(--color-gray-500)', 
                                    fontSize: 'var(--font-size-sm)',
                                    fontStyle: 'italic'
                                }}>
                                    No reminders configured. Click options above to enable email reminders.
                                </p>
                            )}

                            {/* Custom Email Template */}
                            {reminders.length > 0 && (
                                <div style={{ marginTop: 'var(--space-4)', borderTop: '1px solid var(--color-gray-200)', paddingTop: 'var(--space-4)' }}>
                                    <div style={{ display: 'flex', gap: 'var(--space-4)', marginBottom: showEmailTemplate ? 'var(--space-3)' : 0 }}>
                                        <button
                                            type="button"
                                            onClick={() => setShowEmailTemplate(!showEmailTemplate)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: 'var(--color-primary)',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                fontSize: 'var(--font-size-sm)',
                                                padding: 0
                                            }}
                                        >
                                            <Edit size={14} />
                                            {showEmailTemplate ? 'Hide' : 'Customize'} email template
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handlePreviewEmail}
                                            disabled={isLoadingPreview}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: 'var(--color-gray-600)',
                                                cursor: isLoadingPreview ? 'wait' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                fontSize: 'var(--font-size-sm)',
                                                padding: 0,
                                                opacity: isLoadingPreview ? 0.6 : 1
                                            }}
                                        >
                                            <Eye size={14} />
                                            {isLoadingPreview ? 'Loading...' : 'Preview email'}
                                        </button>
                                    </div>

                                    {showEmailTemplate && (
                                        <div>
                                            <p className="form-hint" style={{ marginBottom: 'var(--space-2)' }}>
                                                Customize the reminder email body text. Leave empty to use the default template.
                                                The email will automatically include the meeting date/time and join button.
                                            </p>
                                            <textarea
                                                className="form-textarea"
                                                value={reminderEmailBody}
                                                onChange={(e) => setReminderEmailBody(e.target.value)}
                                                placeholder="Hello,

This is your reminder to join the {{meeting_name}} in {{reminder_time}}.

The link to join the Microsoft Teams screenshare meeting is below, in the calendar invite, as well as a phone number you can dial in to if you don't have a computer microphone."
                                                rows={6}
                                                style={{ fontFamily: 'inherit', fontSize: 'var(--font-size-sm)' }}
                                            />
                                            
                                            {/* Placeholder Reference */}
                                            <details style={{ marginTop: 'var(--space-2)' }}>
                                                <summary style={{ 
                                                    cursor: 'pointer', 
                                                    fontSize: 'var(--font-size-sm)', 
                                                    color: 'var(--color-gray-600)',
                                                    userSelect: 'none'
                                                }}>
                                                    Available placeholders
                                                </summary>
                                                <div style={{ 
                                                    marginTop: 'var(--space-2)', 
                                                    padding: 'var(--space-3)', 
                                                    background: 'white',
                                                    borderRadius: 'var(--radius-sm)',
                                                    fontSize: 'var(--font-size-sm)'
                                                }}>
                                                    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
                                                        <div><code style={{ background: 'var(--color-gray-100)', padding: '2px 6px', borderRadius: '3px' }}>{'{{recipient_name}}'}</code> — Person receiving the reminder</div>
                                                        <div><code style={{ background: 'var(--color-gray-100)', padding: '2px 6px', borderRadius: '3px' }}>{'{{host_name}}'}</code> — Your name (meeting host)</div>
                                                        <div><code style={{ background: 'var(--color-gray-100)', padding: '2px 6px', borderRadius: '3px' }}>{'{{meeting_name}}'}</code> — Name of this scheduling link</div>
                                                        <div><code style={{ background: 'var(--color-gray-100)', padding: '2px 6px', borderRadius: '3px' }}>{'{{meeting_time}}'}</code> — Full date and time</div>
                                                        <div><code style={{ background: 'var(--color-gray-100)', padding: '2px 6px', borderRadius: '3px' }}>{'{{meeting_date}}'}</code> — Just the date</div>
                                                        <div><code style={{ background: 'var(--color-gray-100)', padding: '2px 6px', borderRadius: '3px' }}>{'{{duration}}'}</code> — Meeting duration</div>
                                                        <div><code style={{ background: 'var(--color-gray-100)', padding: '2px 6px', borderRadius: '3px' }}>{'{{reminder_time}}'}</code> — Time until meeting (e.g., "15 minutes")</div>
                                                    </div>
                                                </div>
                                            </details>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Blocking Rules Section */}
                    <button
                        type="button"
                        onClick={() => setShowBlocking(!showBlocking)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-primary)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginBottom: 'var(--space-4)',
                            fontSize: 'var(--font-size-sm)'
                        }}
                    >
                        <Ban size={14} />
                        {showBlocking ? 'Hide' : 'Show'} blocking rules
                        {(blockedDates.length > 0 || blockedTimes.length > 0) && (
                            <span style={{
                                background: 'var(--color-primary)',
                                color: 'white',
                                borderRadius: '50%',
                                width: '18px',
                                height: '18px',
                                fontSize: '11px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginLeft: '4px'
                            }}>
                                {blockedDates.length + blockedTimes.length}
                            </span>
                        )}
                    </button>

                    {showBlocking && (
                        <div style={{ 
                            background: 'var(--color-gray-50)', 
                            padding: 'var(--space-4)', 
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            {/* Blocked Dates */}
                            <div style={{ marginBottom: 'var(--space-4)' }}>
                                <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 'var(--space-2)' }}>
                                    <CalendarX size={16} />
                                    Blocked Dates
                                </h4>
                                <p className="form-hint" style={{ marginBottom: 'var(--space-3)' }}>
                                    Block specific dates (e.g., vacation, holidays)
                                </p>

                                {blockedDates.length > 0 && (
                                    <div style={{ marginBottom: 'var(--space-3)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                                        {blockedDates.map((d, i) => (
                                            <span
                                                key={i}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    background: 'white',
                                                    border: '1px solid var(--color-gray-200)',
                                                    borderRadius: 'var(--radius-full)',
                                                    padding: '4px 8px 4px 12px',
                                                    fontSize: 'var(--font-size-sm)'
                                                }}
                                            >
                                                {d.blocked_date}
                                                {d.reason && <span style={{ color: 'var(--color-gray-500)' }}>({d.reason})</span>}
                                                <button
                                                    type="button"
                                                    onClick={() => removeBlockedDate(i)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--color-gray-400)' }}
                                                >
                                                    <X size={14} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end' }}>
                                    <div style={{ flex: 1 }}>
                                        <input
                                            type="date"
                                            className="form-input"
                                            value={newBlockedDate}
                                            onChange={(e) => setNewBlockedDate(e.target.value)}
                                            min={new Date().toISOString().split('T')[0]}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={newBlockedDateReason}
                                            onChange={(e) => setNewBlockedDateReason(e.target.value)}
                                            placeholder="Reason (optional)"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={addBlockedDate}
                                        className="btn btn-secondary btn-sm"
                                        disabled={!newBlockedDate}
                                    >
                                        <Plus size={16} />
                                    </button>
                                </div>
                            </div>

                            {/* Blocked Times */}
                            <div>
                                <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 'var(--space-2)' }}>
                                    <Clock size={16} />
                                    Blocked Time Periods
                                </h4>
                                <p className="form-hint" style={{ marginBottom: 'var(--space-3)' }}>
                                    Block recurring times (e.g., lunch hour, focus time)
                                </p>

                                {blockedTimes.length > 0 && (
                                    <div style={{ marginBottom: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                        {blockedTimes.map((t, i) => (
                                            <div
                                                key={i}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    background: 'white',
                                                    border: '1px solid var(--color-gray-200)',
                                                    borderRadius: 'var(--radius-sm)',
                                                    padding: 'var(--space-2) var(--space-3)',
                                                    fontSize: 'var(--font-size-sm)'
                                                }}
                                            >
                                                <span>
                                                    <strong>{t.day_of_week === null ? 'Every day' : DAYS_OF_WEEK.find(d => d.value === t.day_of_week)?.label}</strong>
                                                    {' '}from {formatTime(t.start_time)} to {formatTime(t.end_time)}
                                                    {t.reason && <span style={{ color: 'var(--color-gray-500)' }}> ({t.reason})</span>}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeBlockedTime(i)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--color-gray-400)' }}
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 'var(--space-2)', alignItems: 'flex-end' }}>
                                    <div>
                                        <label className="form-label" style={{ fontSize: 'var(--font-size-xs)' }}>Day</label>
                                        <select
                                            className="form-select"
                                            value={newTimeDay === null ? '' : newTimeDay}
                                            onChange={(e) => setNewTimeDay(e.target.value === '' ? null : Number(e.target.value))}
                                        >
                                            {DAYS_OF_WEEK.map(d => (
                                                <option key={d.label} value={d.value === null ? '' : d.value}>{d.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label" style={{ fontSize: 'var(--font-size-xs)' }}>From</label>
                                        <select
                                            className="form-select"
                                            value={newTimeStart}
                                            onChange={(e) => setNewTimeStart(e.target.value)}
                                        >
                                            {TIMES.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label" style={{ fontSize: 'var(--font-size-xs)' }}>To</label>
                                        <select
                                            className="form-select"
                                            value={newTimeEnd}
                                            onChange={(e) => setNewTimeEnd(e.target.value)}
                                        >
                                            {TIMES.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label" style={{ fontSize: 'var(--font-size-xs)' }}>Reason</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={newTimeReason}
                                            onChange={(e) => setNewTimeReason(e.target.value)}
                                            placeholder="Optional"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={addBlockedTime}
                                        className="btn btn-secondary btn-sm"
                                    >
                                        <Plus size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-secondary"
                            style={{ flex: 1 }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="btn btn-primary"
                            style={{ flex: 1 }}
                        >
                            {isSaving ? 'Saving...' : (link ? 'Update' : 'Create')} Link
                        </button>
                    </div>
                </form>
            </div>

            {/* Email Preview Modal */}
            {showPreviewModal && (
                <div 
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1100,
                        padding: 'var(--space-4)'
                    }}
                    onClick={() => setShowPreviewModal(false)}
                >
                    <div 
                        style={{
                            background: 'white',
                            borderRadius: 'var(--radius-lg)',
                            maxWidth: '650px',
                            width: '100%',
                            maxHeight: '90vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: 'var(--space-4)',
                            borderBottom: '1px solid var(--color-gray-200)'
                        }}>
                            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Eye size={20} />
                                Email Preview
                            </h3>
                            <button
                                onClick={() => setShowPreviewModal(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    display: 'flex',
                                    color: 'var(--color-gray-500)'
                                }}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div style={{ 
                            flex: 1, 
                            overflow: 'auto',
                            background: '#f0f0f0'
                        }}>
                            <iframe
                                srcDoc={previewHtml}
                                style={{
                                    width: '100%',
                                    height: '600px',
                                    border: 'none'
                                }}
                                title="Email Preview"
                            />
                        </div>
                        <div style={{
                            padding: 'var(--space-3)',
                            borderTop: '1px solid var(--color-gray-200)',
                            textAlign: 'center'
                        }}>
                            <p style={{ 
                                margin: 0, 
                                fontSize: 'var(--font-size-sm)', 
                                color: 'var(--color-gray-500)' 
                            }}>
                                This is a preview with sample data. Actual emails will use real booking information.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
